"use client";

import { SignInButton, useUser } from "@clerk/nextjs";
import { useState } from "react";

import { css } from "@/../styled-system/css";
import CheckIcon from "@/assets/svg/check.svg";
import LoaderCircle from "@/assets/svg/loader-circle.svg";

import ButtonSkeleton from "./ButtonSkeleton";
import Button from "../ui/Button";
const pricingCardContainerClass = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "8",
  maxWidth: "500px",
  width: "100%",
  height: "full",
  rounded: "2xl",
  shadow: "md",
  borderWidth: "1px",
  borderColor: "gray.200",
  py: "10",
  px: "8",
  mx: "auto",
  wordBreak: "break-word",
});

const checkIconClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
  backgroundColor: "emerald.100",
  flexShrink: "0",
  rounded: "full",
});

interface contentType {
  price: string;
  header: string;
  subhead: string;
  features: string[];
  link: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: any;
}

interface PricingCardProps {
  content: contentType;
  planId?: number;
}

function PricingCard({ content, planId }: PricingCardProps) {
  const { isSignedIn, isLoaded } = useUser();
  const [loadingRequest, setLoadingRequest] = useState(false);

  const handleClick = async () => {
    setLoadingRequest(true);
    if (content.onClick) {
      await content.onClick();
      setLoadingRequest(false); // You can change this to whatever you need after the onClick function completes
    }
  };

  return (
    <div className={pricingCardContainerClass}>
      <div>
        <div className={css({ mb: "5", textAlign: "center" })}>
          <span
            className={css({
              fontSize: "5xl",
              fontWeight: "semibold",
              color: "gray.900",
            })}
          >
            {content.price}
          </span>
        </div>
        <div>
          <div className={css({ mb: "1", textAlign: "center" })}>
            <span
              className={css({
                fontSize: "xl",
                fontWeight: "semibold",
                color: "gray.900",
              })}
            >
              {content.header}
            </span>
          </div>
          <div className={css({ textAlign: "center" })}>
            <span
              className={css({
                color: "slate.600",
              })}
            >
              {content.subhead}
            </span>
          </div>
        </div>
      </div>
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
        })}
      >
        <ul
          className={css({
            display: "block",
            width: "100%",
            height: "100%",
            mb: "8",
          })}
        >
          {content.features.map((feature, index) => (
            <li
              key={index}
              className={css({
                display: "flex",
                flexDirection: "row",
                gap: "3",
                mb: "4",
              })}
            >
              <div className={checkIconClass}>
                <CheckIcon width="1.125rem" strokeWidth={3} stroke="#17B26A" />
              </div>
              <span className={css({ color: "slate.600" })}>{feature}</span>
            </li>
          ))}
        </ul>
        {!isLoaded ? (
          <ButtonSkeleton />
        ) : !isSignedIn ? (
          <SignInButton forceRedirectUrl={content.link}>
            <Button width="fill" variant="brandBlue">
              <span>Get Started</span>
            </Button>
          </SignInButton>
        ) : (
          content.onClick && (
            <Button
              disabled={loadingRequest}
              width="fill"
              onClick={handleClick}
              variant="brandBlue"
            >
              {loadingRequest && (
                <LoaderCircle
                  width={20}
                  className={css({ animation: "spin" })}
                />
              )}
              <span>{planId === 2 ? "Manage" : "Subscribe"}</span>
            </Button>
          )
        )}
      </div>
    </div>
  );
}

export default PricingCard;
